import type { ApiListingItem } from 'src/types/listing';
import type { ApiRentalListingFull } from 'src/types/rental';
import type { DocumentHeadProps } from 'src/components/DocumentHead';
import type { CatalogVertical, CatalogFilterValues } from './catalogUrl';

import { pluralCars } from './pluralCars';
import { carTitle } from './documentTitle';
import { formatImageUrl } from './format-image';
import { buildCatalogPath } from './catalogUrl';
import { fromDailyPrice } from './rentalPricing';
import { hasActiveFilter } from './requestError';
import { formatMoney, formatAmount } from './money';
import { fuelLabel, gearboxLabel } from './carAttributes';

/**
 * What the public pages say of themselves to a search engine and a link preview (storefront-catalog
 * spec §4.5): the title, the description, the page's one address and its picture. Every word is
 * made of the page's own data — the car, its price, how many cars a catalog lists — and the line
 * the storefront already carries; nothing here promises more than the page shows.
 */
type PageHead = Pick<DocumentHeadProps, 'title' | 'description' | 'canonical' | 'image'>;

/** The storefront's own line, the end of every description. */
const TAGLINE = 'GAC — cars, secured by smart contract.';

/** The longest description: a search result shows about this much, and a preview little more. */
export const DESCRIPTION_LIMIT = 200;

/** Cut at a word, with an ellipsis, when the text is longer than a description may be. */
export const clipDescription = (text: string, limit = DESCRIPTION_LIMIT): string => {
  const flat = text.replace(/\s+/g, ' ').trim();

  if (flat.length <= limit) return flat;

  const cut = flat.slice(0, limit - 1);
  const lastSpace = cut.lastIndexOf(' ');

  return `${(lastSpace > limit / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:—-]+$/, '')}…`;
};

const photoOf = (car: { images?: string[] | null } | undefined): string | undefined =>
  car?.images?.[0] ? formatImageUrl(car.images[0]) : undefined;

export interface CatalogHeadInput {
  vertical: CatalogVertical;
  /** The make and the model of the view, by name — once the catalogue has resolved them. */
  makeName?: string;
  modelName?: string;
  makeSlug?: string;
  modelSlug?: string;
  filters?: CatalogFilterValues;
  page: number;
  /** How many cars the view lists; `undefined` until the api has said. */
  total?: number;
  /** A dealer's page: the dealer's id, and the name once a car of theirs has brought it. */
  dealerId?: string;
  dealerName?: string;
  /** The first car on the page: its photo is the picture of the page's preview. */
  firstCar?: { images?: string[] | null };
}

/**
 * A catalog page. Its address is the view's own (`buildCatalogPath`) — the page included — unless a
 * facet narrows it: a price range or a body type is a way of looking at a list, not a page of its
 * own, and is filed under the list itself.
 */
export const catalogHead = ({
  vertical,
  makeName,
  modelName,
  makeSlug,
  modelSlug,
  filters,
  page,
  total,
  dealerId,
  dealerName,
  firstCar,
}: CatalogHeadInput): PageHead => {
  const offer = vertical === 'buy' ? 'for sale' : 'for rent';
  const subject = [makeName, makeName ? modelName : undefined].filter(Boolean).join(' ');
  const by = dealerName ? ` by ${dealerName}` : '';
  // Whose cars the page lists: the dealer's by name, one dealer's while the name is not known
  // (a dealer's page is never "from dealers"), every dealer's on the catalog itself.
  const from = by || (dealerId ? ' from one dealer' : ' from dealers');
  const narrowed = hasActiveFilter(filters);
  const counted = total ? `${subject ? `${formatAmount(total)} ${subject}` : pluralCars(total)}` : subject || 'Cars';

  return {
    title: [`${subject || 'Cars'} ${offer}${by}`, page > 1 ? `Page ${page}` : undefined],
    description: `${counted} ${offer}${from} on ${TAGLINE}`,
    canonical: buildCatalogPath({ vertical, dealerId, makeSlug, modelSlug, page: narrowed ? 1 : page }),
    image: photoOf(firstCar),
  };
};

/** A car for sale: what it is, what it costs, what the dealer said of it. */
export const saleListingHead = (item: ApiListingItem, descriptionText: string): PageHead => {
  const facts = [
    formatMoney(item.price, item.currency),
    item.mileage != null ? `${formatAmount(item.mileage)} km` : undefined,
    gearboxLabel(item.gearbox),
    fuelLabel(item.fuel),
  ].filter(Boolean);

  return {
    title: carTitle(item),
    description: clipDescription(`${carTitle(item)} for sale: ${facts.join(', ')}. ${descriptionText}`),
    canonical: `/listing/${item.id}`,
    image: photoOf(item),
  };
};

/** A car for rent: what it is, the daily rate and the deposit, where it is. */
export const rentalListingHead = (listing: ApiRentalListingFull, descriptionText: string): PageHead => {
  const { price, isFrom } = fromDailyPrice(listing);
  const location = listing.location?.trim();
  const facts = [
    `${isFrom ? 'from ' : ''}${formatMoney(price, listing.currency)} a day`,
    `deposit ${formatMoney(listing.depositAmount, listing.currency)}`,
    location,
  ].filter(Boolean);

  return {
    title: [carTitle(listing), 'Rent'],
    description: clipDescription(`${carTitle(listing)} for rent: ${facts.join(', ')}. ${descriptionText}`),
    canonical: `/rentals/listing/${listing.id}`,
    image: photoOf(listing),
  };
};
